import { useRef, type PointerEvent } from 'react';
import { formatScanDate } from '../formatScanDate';
import { getMarkerTone } from '../scanInstructionTone';
import { MINIMAP_HEIGHT, MINIMAP_TICK_WIDTH } from './geometry';
import { scaleX, type ScaleDomain, type TimelineScan } from './scanScale';

type DragMode = 'pan' | 'start' | 'end';

interface TimelineMinimapProps {
    full: ScaleDomain;
    viewport: ScaleDomain;
    ticks: TimelineScan[];
    width: number;
    onPan: (deltaMs: number) => void;
    onResize: (edge: 'start' | 'end', time: number) => void;
    onCentreOn: (time: number) => void;
    onFit: () => void;
    /** False on a case with no aligners: the ticks carry no decision to colour. */
    tracksAligners?: boolean;
}

const HANDLE_WIDTH = 10;
const EDGE_INSET = 2;
// At deep zoom the real window is a couple of pixels wide; without a floor the two
// handles land on top of each other and the later one swallows every pointerdown.
const MIN_WINDOW_WIDTH = 2 * HANDLE_WIDTH;

function TimelineMinimap({
    full,
    viewport,
    ticks,
    width,
    onPan,
    onResize,
    onCentreOn,
    onFit,
    tracksAligners = true
}: TimelineMinimapProps) {
    const stripRef = useRef<HTMLDivElement>(null);
    const drag = useRef<{ mode: DragMode; clientX: number } | null>(null);

    const startX = scaleX(viewport.start, full, width);
    const endX = scaleX(viewport.end, full, width);
    // Inset so the rounded window and its handles never bleed into the panel border.
    const inset = (x: number) => Math.max(EDGE_INSET, Math.min(x, width - EDGE_INSET));
    const minWindow = Math.min(MIN_WINDOW_WIDTH, Math.max(0, width - 2 * EDGE_INSET));
    const rawRight = Math.max(inset(startX) + minWindow, inset(endX));
    // Pinned to the far right the floor would overflow the strip, so shift both edges
    // back rather than letting the window collapse under the handles again.
    const overflow = Math.max(0, rawRight - (width - EDGE_INSET));
    const windowLeft = Math.max(EDGE_INSET, inset(startX) - overflow);
    const windowRight = rawRight - overflow;
    const handleLeft = (x: number) =>
        Math.min(Math.max(x - HANDLE_WIDTH / 2, 0), Math.max(0, width - HANDLE_WIDTH));

    const timeAt = (clientX: number): number => {
        const rect = stripRef.current?.getBoundingClientRect();
        if (!rect || rect.width <= 0) return full.start;
        return full.start + ((clientX - rect.left) / rect.width) * (full.end - full.start);
    };

    const beginDrag = (mode: DragMode) => (event: PointerEvent<HTMLDivElement>) => {
        event.stopPropagation();
        drag.current = { mode, clientX: event.clientX };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const active = drag.current;
        if (!active) return;
        if (active.mode === 'pan') {
            const msPerPx = width > 0 ? (full.end - full.start) / width : 0;
            onPan((event.clientX - active.clientX) * msPerPx);
            active.clientX = event.clientX;
            return;
        }
        onResize(active.mode, timeAt(event.clientX));
    };

    const endDrag = (event: PointerEvent<HTMLDivElement>) => {
        if (!drag.current) return;
        drag.current = null;
        // On pointercancel the browser has already dropped the capture, and releasing
        // an inactive pointer throws.
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    const handleProps = (edge: 'start' | 'end') => ({
        role: 'slider' as const,
        'aria-label': edge === 'start' ? 'Range start, drag to scope' : 'Range end, drag to scope',
        'aria-valuemin': full.start,
        'aria-valuemax': full.end,
        'aria-valuenow': edge === 'start' ? viewport.start : viewport.end,
        // Without this a screen reader reads the raw epoch milliseconds.
        'aria-valuetext': formatScanDate(
            new Date(edge === 'start' ? viewport.start : viewport.end).toISOString()
        ),
        tabIndex: -1,
        onPointerDown: beginDrag(edge),
        onPointerMove,
        onPointerUp: endDrag,
        onPointerCancel: endDrag,
        className:
            'absolute inset-y-[2px] z-10 flex cursor-ew-resize touch-none items-center justify-center'
    });

    return (
        <div
            ref={stripRef}
            onPointerDown={(event) => onCentreOn(timeAt(event.clientX))}
            className="relative w-full cursor-pointer overflow-hidden bg-gray-50"
            style={{ height: MINIMAP_HEIGHT }}
        >
            {ticks.map((tick) => (
                <div
                    key={tick.scan.scanId}
                    aria-hidden
                    className={`absolute -translate-x-1/2 rounded-sm border ${
                        tick.isSelected
                            ? 'border-gray-900 bg-gray-900'
                            : getMarkerTone(tick.scan, tracksAligners).base
                    }`}
                    style={{
                        top: 4,
                        left: tick.x,
                        width: tick.isSelected ? MINIMAP_TICK_WIDTH + 1 : MINIMAP_TICK_WIDTH,
                        height: MINIMAP_HEIGHT - 8
                    }}
                />
            ))}

            <div
                aria-hidden
                className="absolute inset-y-0 left-0 bg-white/60"
                style={{ width: Math.max(0, startX) }}
            />
            <div
                aria-hidden
                className="absolute inset-y-0 bg-white/60"
                style={{ left: endX, width: Math.max(0, width - endX) }}
            />

            <div
                aria-label="Visible date range, drag to pan, double-click to show all"
                onPointerDown={beginDrag('pan')}
                onDoubleClick={onFit}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                className="absolute inset-y-[2px] cursor-grab touch-none rounded-md border-2 border-gray-300 bg-gray-900/[0.04] active:cursor-grabbing"
                style={{ left: windowLeft, width: windowRight - windowLeft }}
            />

            <div
                {...handleProps('start')}
                style={{ left: handleLeft(windowLeft), width: HANDLE_WIDTH }}
            >
                <div className="h-2 w-[3px] rounded-full bg-gray-400" />
            </div>
            <div
                {...handleProps('end')}
                style={{ left: handleLeft(windowRight), width: HANDLE_WIDTH }}
            >
                <div className="h-2 w-[3px] rounded-full bg-gray-400" />
            </div>
        </div>
    );
}

export { TimelineMinimap };
